import * as React from 'react';

import { useDebounceFn } from 'ahooks';
import { Col, Form, Input, Row } from 'antd';

import { AntdUploadImgCrop, RichTextEditor } from 'components';
import { useBrandAction, useBrandValue } from 'context';
import { generateDownloadFile, inputRule } from 'utils';

type FieldType = Pick<
  ReturnType<typeof useBrandValue>,
  'title' | 'slug' | 'iconId' | 'description'
>;

const BrandBaseInfo = () => {
  const { title, slug, iconId, description, ownerType } = useBrandValue();
  const action = useBrandAction();

  const { run: debounceUpdateTitle } = useDebounceFn(
    (debouncedTitle: typeof title) => {
      action({
        type: 'UPDATE_BRAND_TITLE',
        payload: {
          title: debouncedTitle,
        },
      });
    },
  );
  const { run: debounceUpdateSlug } = useDebounceFn(
    (debouncedSlug: typeof slug) => {
      action({
        type: 'UPDATE_BRAND_SLUG',
        payload: {
          slug: debouncedSlug,
        },
      });
    },
  );
  const { run: debounceUpdateDescription } = useDebounceFn(
    (debouncedDescription: typeof description) => {
      action({
        type: 'UPDATE_BRAND_DESCRIPTION',
        payload: {
          description: debouncedDescription,
        },
      });
    },
  );

  if (!ownerType) return <React.Fragment />;

  return (
    <Row>
      <Col span={24}>
        <Form.Item<FieldType>
          label='عنوان'
          name='title'
          rules={[inputRule.required, inputRule.string]}
        >
          <Input
            placeholder='عنوان برند را وارد کنید'
            onChange={({ target: { value } }) => {
              debounceUpdateTitle(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='پیوند یکتا (Slug)'
          name='slug'
          rules={[inputRule.required, inputRule.nonDot]}
        >
          <Input
            placeholder='پیوند یکتا برند را وارد کنید'
            onChange={({ target: { value } }) => {
              debounceUpdateSlug(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='نشان‌واره (Icon)'
          name='iconId'
          valuePropName='file'
        >
          <AntdUploadImgCrop
            ownerType={ownerType}
            fileList={
              iconId
                ? [
                    {
                      uid: iconId,
                      name: 'نشان‌واره برند',
                      status: 'done',
                      url: generateDownloadFile(ownerType, iconId),
                    },
                  ]
                : undefined
            }
            onChange={({ file: { status, response } }) => {
              action({
                type: 'UPDATE_BRAND_ICON',
                payload: {
                  iconId: undefined,
                },
              });

              if (status === 'done' && response) {
                const { id } = response;
                action({
                  type: 'UPDATE_BRAND_ICON',
                  payload: {
                    iconId: id,
                  },
                });
              }
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='توضیحات' name='description'>
          <RichTextEditor
            ownerType={ownerType}
            options={{
              content: description,
              onUpdate: ({ editor }) => {
                debounceUpdateDescription(editor.getHTML());
              },
            }}
            config={{
              placeholder: {
                placeholder: 'توضیحات برند را وارد کنید',
              },
            }}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { BrandBaseInfo };
